import { Attribute, DocumentProcessStatus, TranscriptDocument, ATTRIBUTE_VALUES } from '~/types/transcriptExtraction';

/**
 * Resolves the final value from an Attribute
 *
 * @param attribute - The attribute object with multiple value sources
 * @returns The resolved value or undefined
 */
export function resolveAttributeValue<T>(attribute: Attribute<T> | undefined): T | undefined {
  if (!attribute) return undefined;

  for (const key of ATTRIBUTE_VALUES) {
    const value = attribute[key];
    if (value !== undefined && value !== null) {
      return value;
    }
  }

  return undefined;
}

/**
 * Checks if an attribute has low confidence in its extracted value
 *
 * @param attribute - The attribute to check
 * @returns True if the extracted value has low confidence
 */
export function hasLowConfidence<T>(attribute: Attribute<T> | undefined): boolean {
  if (!attribute) return false;
  return attribute.extractedValueConfidence === 'low';
}

/**
 * Checks if the document extraction is pending
 *
 * @param document - The education history document
 * @returns True if extraction is pending
 */
export function isExtractionPending(document: TranscriptDocument | undefined): boolean {
  if (!document?.attributes.documentProcessStatus) return false;
  return (
    document.attributes.documentProcessStatus === DocumentProcessStatus.PENDING ||
    document.attributes.documentProcessStatus === DocumentProcessStatus.DOCUMENT_TYPE_VALIDATION_STARTED ||
    document.attributes.documentProcessStatus === DocumentProcessStatus.DOCUMENT_DATA_EXTRACTION_STARTED ||
    document.attributes.documentProcessStatus === DocumentProcessStatus.DOCUMENT_TYPE_VALIDATION_COMPLETED
  );
}

/**
 * Checks if the document validation has failed
 *
 * @param document - The education history document
 * @returns True if validation failed
 */
export function isDocumentTypeValidationFailed(document: TranscriptDocument | undefined): boolean {
  if (!document?.attributes.documentProcessStatus) return false;
  return document.attributes.documentProcessStatus === DocumentProcessStatus.DOCUMENT_TYPE_VALIDATION_FAILED;
}

/**
 * Checks if the document extraction has failed
 *
 * @param document - The education history document
 * @returns True if extraction failed
 */
export function isExtractionFailed(document: TranscriptDocument | undefined): boolean {
  if (!document?.attributes.documentProcessStatus) return false;
  return document.attributes.documentProcessStatus === DocumentProcessStatus.FAILED;
}

/**
 * Checks if the document extraction is complete
 *
 * @param document - The education history document
 * @returns True if extraction is complete
 */
export function isExtractionComplete(document: TranscriptDocument | undefined): boolean {
  if (!document?.attributes.documentProcessStatus) return false;
  return document.attributes.documentProcessStatus === DocumentProcessStatus.COMPLETED;
}

/**
 * Checks if the document extraction was not attempted
 * This happens when transcript AI processing is disabled or unavailable
 *
 * @param document - The education history document
 * @returns True if extraction was not attempted
 */
export function isExtractionNotAttempted(document: TranscriptDocument | undefined): boolean {
  if (!document?.attributes.documentProcessStatus) return false;
  return (
    document.attributes.documentProcessStatus === DocumentProcessStatus.UPLOADED_NOT_PROCESSED ||
    document.attributes.documentProcessStatus === DocumentProcessStatus.UPLOADED_AI_SERVICE_UNAVAILABLE
  );
}

/**
 * Gets the appropriate intent for AccordionWithIntent based on extraction status
 *
 * @param document - The education history document
 * @returns The intent type for the accordion
 */
export function getExtractionIntent(document: TranscriptDocument | undefined): 'positive' | 'warning' | 'secondary' {
  if (!document) return 'secondary';

  if (isExtractionFailed(document)) {
    return 'warning';
  }

  if (isExtractionComplete(document)) {
    return 'positive';
  }

  return 'secondary';
}

/**
 * Gets the title for the extraction accordion based on status
 *
 * @param document - The education history document
 * @returns The title text
 */
export function getExtractionTitle(document: TranscriptDocument | undefined): string {
  if (!document) return 'Transcript Details';

  if (isExtractionPending(document)) {
    return 'Processing transcript...';
  }

  if (isExtractionFailed(document)) {
    return 'Extraction unavailable';
  }

  if (isExtractionComplete(document)) {
    return 'Extracted Transcript Details';
  }

  return 'Transcript Details';
}

/**
 * Finds a transcript document by document ID
 *
 * @param documents - Array of education history documents
 * @param documentKey - The document key/ID to find
 * @returns The matching document or undefined
 */
export function findTranscriptDocument(
  documents: TranscriptDocument[] | undefined,
  documentKey: string
): TranscriptDocument | undefined {
  if (!documents) return undefined;
  return documents.find((doc) => doc.id === documentKey);
}

/**
 * Checks if a document is a transcript
 *
 * @param document - The education history document
 * @returns True if the document is a transcript
 */
export function isTranscript(document: TranscriptDocument | undefined): boolean {
  if (!document) return false;
  return document.type === 'transcript' && document.attributes.documentType === 'TRANSCRIPT';
}

/**
 * Formats a date string for display
 *
 * @param dateStr - The date string (supports YYYY-MM or full ISO date)
 * @returns Formatted date string (e.g., "June 2014" or "January 1, 2023")
 */
export function formatTranscriptDate(dateStr: string | undefined): string {
  if (!dateStr) return 'N/A';

  try {
    // Handle YYYY-MM format
    if (/^\d{4}-\d{2}$/.exec(dateStr)) {
      const [year, month] = dateStr.split('-');
      const date = new Date(Number.parseInt(year, 10), Number.parseInt(month, 10) - 1);
      return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long' }).format(date);
    }

    const date = new Date(dateStr);
    if (Number.isNaN(date.getTime())) {
      return dateStr;
    }
    return new Intl.DateTimeFormat('en-US', { year: 'numeric', month: 'long', day: 'numeric' }).format(date);
  } catch {
    return dateStr;
  }
}
